import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { JOURNAL_ENTRY_TYPES, RULE_KEYS } from 'shared';
import type { CreateJournalEntryInput, JournalEntryType, RuleKey } from 'shared';

import type { JournalEntryDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface JournalEntryEditorProps {
  entry?: JournalEntryDto;
  defaultType?: JournalEntryType;
  onSubmit: (input: CreateJournalEntryInput) => void;
  isSubmitting?: boolean;
}

const emotionalStates = ['Calm', 'Anxious', 'Confident', 'Fearful', 'Greedy', 'Disciplined'];

export function JournalEntryEditor({
  entry,
  defaultType = 'MID_TRADE',
  onSubmit,
  isSubmitting = false,
}: JournalEntryEditorProps) {
  const [entryType, setEntryType] = useState<JournalEntryType>(entry?.entryType ?? defaultType);
  const [body, setBody] = useState(entry?.body ?? '');
  const [emotionalState, setEmotionalState] = useState(entry?.emotionalState ?? 'Calm');
  const [tags, setTags] = useState(entry?.tags.join(', ') ?? '');
  const [rulesFollowed, setRulesFollowed] = useState<RuleKey[]>(
    filterRuleKeys(entry?.rulesFollowed ?? []),
  );
  const [rulesBroken, setRulesBroken] = useState<RuleKey[]>(
    filterRuleKeys(entry?.rulesBroken ?? []),
  );
  const [outcomeR, setOutcomeR] = useState(entry?.outcomeR ?? '');
  const [whatWorkedWell, setWhatWorkedWell] = useState(entry?.whatWorkedWell ?? '');
  const [whatWentWrong, setWhatWentWrong] = useState(entry?.whatWentWrong ?? '');
  const [lessons, setLessons] = useState(entry?.lessons ?? '');
  const [emotionalRegret, setEmotionalRegret] = useState(entry?.emotionalRegret ?? 3);
  const submitLocked = useRef(false);

  const isAutoCreated = entry?.entryType === 'PRE_TRADE';
  const isPostTrade = entryType === 'POST_TRADE';

  useEffect(() => {
    if (!isSubmitting) submitLocked.current = false;
  }, [isSubmitting]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (submitLocked.current) return;
    submitLocked.current = true;
    onSubmit({
      accountId: entry?.accountId ?? undefined,
      symbol: entry?.symbol ?? undefined,
      tradePlanId: entry?.tradePlanId ?? undefined,
      tradeId: entry?.tradeId ?? undefined,
      entryType,
      body,
      emotionalState,
      tags: tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      ...(isPostTrade
        ? {
            rulesFollowed,
            rulesBroken,
            outcomeR: outcomeR.trim() === '' ? null : Number(outcomeR),
            whatWorkedWell,
            whatWentWrong,
            lessons,
            emotionalRegret,
          }
        : {}),
    });
  }

  return (
    <form className="space-y-4" onSubmit={submit}>
      <label className="block text-sm">
        Entry type
        <select
          className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
          value={entryType}
          disabled={isAutoCreated}
          aria-label="Entry type"
          onChange={(event) => setEntryType(event.target.value as JournalEntryType)}
        >
          {JOURNAL_ENTRY_TYPES.map((type) => (
            <option key={type} value={type}>
              {type.replace('_', ' ')}
            </option>
          ))}
        </select>
      </label>

      {isPostTrade ? (
        <PostTradeFields
          rulesFollowed={rulesFollowed}
          rulesBroken={rulesBroken}
          outcomeR={outcomeR}
          whatWorkedWell={whatWorkedWell}
          whatWentWrong={whatWentWrong}
          lessons={lessons}
          emotionalRegret={emotionalRegret}
          setRulesFollowed={setRulesFollowed}
          setRulesBroken={setRulesBroken}
          setOutcomeR={setOutcomeR}
          setWhatWorkedWell={setWhatWorkedWell}
          setWhatWentWrong={setWhatWentWrong}
          setLessons={setLessons}
          setEmotionalRegret={setEmotionalRegret}
        />
      ) : (
        <>
          <label className="block text-sm">
            Body
            <textarea
              className="mt-1 min-h-36 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
              aria-label="Journal body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </label>
          <label className="block text-sm">
            Emotional state
            <select
              className="mt-1 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
              value={emotionalState}
              aria-label="Emotional state"
              onChange={(event) => setEmotionalState(event.target.value)}
            >
              {emotionalStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm" htmlFor="journal-tags">
            Tags
            <Input
              id="journal-tags"
              aria-label="Tags"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
            />
          </label>
        </>
      )}

      <Button type="submit" disabled={isSubmitting}>
        Save journal entry
      </Button>
    </form>
  );
}

interface PostTradeFieldsProps {
  rulesFollowed: RuleKey[];
  rulesBroken: RuleKey[];
  outcomeR: string;
  whatWorkedWell: string;
  whatWentWrong: string;
  lessons: string;
  emotionalRegret: number;
  setRulesFollowed: (value: RuleKey[]) => void;
  setRulesBroken: (value: RuleKey[]) => void;
  setOutcomeR: (value: string) => void;
  setWhatWorkedWell: (value: string) => void;
  setWhatWentWrong: (value: string) => void;
  setLessons: (value: string) => void;
  setEmotionalRegret: (value: number) => void;
}

function PostTradeFields(props: PostTradeFieldsProps) {
  return (
    <div className="space-y-4">
      <ChipGroup
        label="Rules followed"
        value={props.rulesFollowed}
        onChange={props.setRulesFollowed}
      />
      <ChipGroup label="Rules broken" value={props.rulesBroken} onChange={props.setRulesBroken} />
      <label className="block text-sm" htmlFor="journal-outcome-r">
        Outcome R
        <Input
          id="journal-outcome-r"
          type="number"
          step="0.1"
          value={props.outcomeR}
          onChange={(event) => props.setOutcomeR(event.target.value)}
        />
      </label>
      <Textarea
        label="What worked well"
        value={props.whatWorkedWell}
        onChange={props.setWhatWorkedWell}
      />
      <Textarea
        label="What went wrong"
        value={props.whatWentWrong}
        onChange={props.setWhatWentWrong}
      />
      <Textarea
        label="One lesson, written as an instruction to yourself"
        value={props.lessons}
        onChange={props.setLessons}
      />
      <div className="space-y-2">
        <span className="text-sm">Emotional regret</span>
        <div className="flex gap-1" role="radiogroup" aria-label="Emotional regret">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              className={rating <= props.emotionalRegret ? 'text-warning' : 'text-text-muted'}
              aria-label={`${rating} regret`}
              onClick={() => props.setEmotionalRegret(rating)}
            >
              *
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChipGroup({
  label,
  value,
  onChange,
}: {
  label: string;
  value: RuleKey[];
  onChange: (value: RuleKey[]) => void;
}) {
  return (
    <div className="space-y-2">
      <span className="text-sm">{label}</span>
      <div className="flex flex-wrap gap-2">
        {RULE_KEYS.map((rule) => {
          const selected = value.includes(rule);
          return (
            <button
              key={rule}
              type="button"
              className={`rounded border px-2 py-1 text-xs ${
                selected
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border text-text-muted'
              }`}
              onClick={() =>
                onChange(selected ? value.filter((item) => item !== rule) : [...value, rule])
              }
            >
              {rule}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function filterRuleKeys(values: string[]): RuleKey[] {
  return values.filter((value): value is RuleKey => RULE_KEYS.some((ruleKey) => ruleKey === value));
}

function Textarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm">
      {label}
      <textarea
        className="mt-1 min-h-24 w-full rounded border border-border bg-bg px-3 py-2 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
